/**
 * @fileoverview Zod schemas and types for unmatched product listings.
 * @module features/sku-alias/schemas/unmatchedListingSchema
 */

import { z } from 'zod'

/**
 * Branded identifier for an unmatched listing row.
 */
export const listingIdSchema = z.string().brand<'ListingId'>()
export type ListingId = z.infer<typeof listingIdSchema>

/**
 * Branded identifier for an existing product within the catalog.
 */
export const productIdSchema = z.number().int().nonnegative().brand<'ProductId'>()
export type ProductId = z.infer<typeof productIdSchema>

const stagingListingSchema = z.object({
  type: z.literal('staging_external_sku'),
  staging: z.object({
    externalSlug: z.string(),
    externalProductName: z.string().optional().nullable().catch(undefined),
    sources: z.array(z.string()).default([]),
    entryIds: z.array(z.number().int().nonnegative()).default([]),
    firstSeen: z.string(),
    lastSeen: z.string(),
    occurrenceCount: z.number().int().nonnegative(),
    totalUnattributed: z.number().nonnegative().default(0),
    unattributedRevenue: z.number().nonnegative().default(0),
    unattributedAdSpend: z.number().nonnegative().default(0),
    unattributedQuantity: z.number().int().nonnegative().default(0),
    currencyCode: z.string().optional().nullable().catch(undefined),
    // AI suggestion fields
    suggestedVariantId: z.number().nullable().optional().catch(null),
    suggestedVariantSkuKey: z.string().nullable().optional().catch(null),
    suggestedVariantName: z.string().nullable().optional().catch(null),
  }),
})

const storeVariantListingSchema = z.object({
  type: z.literal('store_variant_unresolved'),
  storeVariant: z.object({
    id: z.number().int().nonnegative(),
    store: z.object({
      id: z.number().int().nonnegative(),
      name: z.string(),
      platformType: z.string(),
      platformDomain: z.string().optional().nullable().catch(undefined),
    }),
    canonicalPath: z.string(),
    productSlug: z.string().optional().nullable().catch(undefined),
    externalVariantId: z.string().optional().nullable().catch(undefined),
    externalSku: z.string().optional().nullable().catch(undefined),
    source: z.string().optional().nullable().catch(undefined),
    createdAt: z.string(),
    missingSku: z.boolean(),
  }),
})

const needsReviewItemSchema = z.discriminatedUnion('type', [
  stagingListingSchema,
  storeVariantListingSchema,
])

/**
 * Unmatched listing schema describing unresolved external identifiers.
 */
export const unmatchedListingSchema = needsReviewItemSchema.transform(data => {
  if (data.type === 'staging_external_sku') {
    const staging = data.staging
    return {
      ...staging,
      type: data.type,
      id: listingIdSchema.parse(`staging:${staging.externalSlug}`),
      source: staging.sources[0] ?? 'unknown',
      canResolve: staging.externalSlug.length > 0,
      canCreate: staging.entryIds.length > 0,
    }
  }

  const storeVariant = data.storeVariant
  const externalSlug = storeVariant.externalSku ?? storeVariant.canonicalPath
  const sources = storeVariant.source ? [storeVariant.source] : []
  const canResolve = !storeVariant.missingSku && Boolean(storeVariant.externalSku)

  return {
    type: data.type,
    id: listingIdSchema.parse(`store-variant:${storeVariant.id}`),
    externalSlug,
    externalProductName: storeVariant.productSlug ?? undefined,
    sources,
    entryIds: [],
    firstSeen: storeVariant.createdAt,
    lastSeen: storeVariant.createdAt,
    occurrenceCount: 0,
    totalUnattributed: 0,
    unattributedRevenue: 0,
    unattributedAdSpend: 0,
    unattributedQuantity: 0,
    currencyCode: undefined,
    suggestedVariantId: null,
    suggestedVariantSkuKey: null,
    suggestedVariantName: null,
    storeVariantId: storeVariant.id,
    storeId: storeVariant.store.id,
    storeName: storeVariant.store.name,
    platformType: storeVariant.store.platformType,
    platformDomain: storeVariant.store.platformDomain ?? undefined,
    canonicalPath: storeVariant.canonicalPath,
    productSlug: storeVariant.productSlug ?? undefined,
    externalVariantId: storeVariant.externalVariantId ?? undefined,
    externalSku: storeVariant.externalSku ?? undefined,
    source: storeVariant.source ?? 'unknown',
    createdAt: storeVariant.createdAt,
    missingSku: storeVariant.missingSku,
    canResolve,
    canCreate: false,
  }
})
export type UnmatchedListing = z.infer<typeof unmatchedListingSchema>

/**
 * Paginated response schema for unmatched listings endpoint.
 */
export const unmatchedListingPageSchema = z.object({
  content: z.array(unmatchedListingSchema),
  totalElements: z.number().optional().catch(undefined),
  totalPages: z.number().optional().catch(undefined),
  size: z.number().optional().catch(undefined),
  number: z.number().optional().catch(undefined),
  first: z.boolean().optional().catch(undefined),
  last: z.boolean().optional().catch(undefined),
})
export type UnmatchedListingPage = z.infer<typeof unmatchedListingPageSchema>
